import type { LegendListRef } from '@legendapp/list/react-native';
import { Stack, useLocalSearchParams } from 'expo-router';
import { useRef, useState } from 'react';
import type { ScrollView } from 'react-native';
import { useQuery } from 'urql';
import Avatar from '@/components/Custom/Avatar';
import Nav from '@/components/Custom/Nav';
import * as NavItem from '@/components/Custom/NavItem';
import Box from '@/components/UI/Box';
import Text from '@/components/UI/Text';
import List from '@/components/Unistyles/List';
import { graphql } from '@/gql/graphql';
import formatMessageDatetime from '@/utils/formatMessageDatetime';

const GetChatMessageDetailReactions = graphql(
  /* GraphQL */ `
    query GetChatMessageDetailReactions(
      $messageId: uuid!
      $limit: Int!
      $where: message_reaction_bool_exp!
    ) {
      message_by_pk(id: $messageId) {
        id
        reaction_count
        reactions(
          limit: $limit
          order_by: { created_at: desc }
          where: $where
        ) {
          id
          content
          created_at
          person {
            id
            name
            avatar_id
            color
          }
        }
      }
    }
  `,
);

const LIMIT = 100;

export default function ChatMessageDetailReactions() {
  const listRef = useRef<LegendListRef>(null);
  const [selectedReaction, setSelectedReaction] = useState<string | null>(null);
  const navRef = useRef<ScrollView>(null);

  const { messageId } = useLocalSearchParams<{
    messageId?: string;
  }>();

  const [{ data }] = useQuery({
    query: GetChatMessageDetailReactions,
    requestPolicy: 'cache-and-network',
    variables: {
      messageId: messageId!,
      where: selectedReaction ? { content: { _eq: selectedReaction } } : {},
      limit: LIMIT,
    },
  });

  const reactionCounts: { [key: string]: number }[] = [
    { All: 0 },
    ...(data?.message_by_pk?.reaction_count || []),
  ];

  return (
    <Box flex>
      <Stack.Screen options={{ title: 'Reactions' }} />
      <Nav
        ref={navRef}
        data={reactionCounts}
        keyExtractor={(item) => Object.keys(item)[0] ?? ''}
        renderItem={({ item, index }) => {
          const [content, count] = Object.entries(item)[0] || [];
          const label = content !== 'All' ? content : null;

          return (
            <NavItem.Container
              index={index}
              isFocused={
                selectedReaction === content ||
                (content === 'All' && selectedReaction === null)
              }
              onPress={() => setSelectedReaction(label)}
            >
              {content !== 'All' ? (
                <NavItem.Label>{count}</NavItem.Label>
              ) : null}
              <Text size="lg">{content}</Text>
            </NavItem.Container>
          );
        }}
      />
      <Box flex>
        <List
          ref={listRef}
          data={data?.message_by_pk?.reactions || []}
          keyExtractor={(item) => item.id}
          renderItem={({ item }) => {
            return (
              <Box
                px="xl"
                py="md"
                space="md"
                direction="horizontal"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box direction="horizontal" space="md" alignItems="center">
                  <Avatar
                    id={item.person.avatar_id ?? ''}
                    size="md"
                    name={item.person.name ?? ''}
                    color={item.person.color}
                  />
                  <Box>
                    <Text size="xl">{item.person.name}</Text>
                    <Text variant="typographySecondary" size="sm">
                      {formatMessageDatetime(item.created_at)}
                    </Text>
                  </Box>
                </Box>

                <Text size="2xl">{item.content}</Text>
              </Box>
            );
          }}
        />
      </Box>
    </Box>
  );
}
